import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useRoleAuthentication from '../../Authentication/hooks/useRoleAuthentication';
import LoadingSpinner from '../../Common/components/LoadingSpinner';
import { ADMIN_CONFIG } from '../../config/adminConfig';

/**
 * RoleBasedRedirect Component
 * 
 * Handles role-based routing from the root path ("/") to appropriate dashboards:
 * - Admins (full admin with ADMIN_ACCESS role) -> /admin/dashboard
 * - Student employees -> configurable (admin dashboard or student dashboard)
 * - Students -> /dashboard
 * 
 * This prevents admins from seeing irrelevant "no application" messages
 * and ensures each user type lands where they need to be.
 */
function RoleBasedRedirect() {
  const navigate = useNavigate();
  const { 
    isLoggedIn, 
    isAdmin,
    isStudentEmployee, 
    isLoading 
  } = useRoleAuthentication();

  useEffect(() => {
    if (!isLoading && isLoggedIn) {
      // Redirect based on user role
      if (isAdmin) {
        // Full admins go to admin dashboard
        navigate('/admin/dashboard', { replace: true });
      } else if (isStudentEmployee && ADMIN_CONFIG.STUDENT_EMPLOYEE_REDIRECT_TO_ADMIN) {
        // Student employees can be configured to go to admin dashboard
        navigate('/admin/dashboard', { replace: true });
      } else {
        // Students and student employees (when not configured for admin) go to student dashboard
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isLoading, isLoggedIn, isAdmin, isStudentEmployee, navigate]);

  // Show loading while determining redirect
  if (isLoading || !isLoggedIn) {
    return <LoadingSpinner isFetching={true} count={1} height="10rem" />;
  }

  // This should not render as redirect happens immediately
  return null;
}

export default RoleBasedRedirect;